import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AgxButton,
  AgxCollapsible,
  AgxModal,
  AgxSpacer,
  AgxTextInput,
} from '@urbanx/agx.ui-components2'
import {
  CustomConditionModel,
  SpecialConditionData,
} from '@urbanx/agx.campaigns.client'
import CustomSpecialCondition from './CustomSpecialCondition'
import { getModelFromCustomCondition } from './getModelFromCustomCondition'
import { updateModelOnCustomCondition } from './updateModelOnCustomCondition'
import { getDefaultCustomCondition } from './getDefaultCustomCondition'

interface AddSpecialConditionButtonProps {
  id: string
  label: string
  existingConditions: SpecialConditionData[]
  onAddSpecialCondition: (
    _specialCondition: SpecialConditionData
  ) => void | Promise<void>
  campaignId: string
  buttonVariant?: 'primary' | 'hollow' | 'dark'
  mode?: 'modal' | 'inline'
  registerBeforeSave?: (
    handler: (() => boolean | Promise<boolean>) | undefined
  ) => void
}

export function AddSpecialConditionButton({
  id,
  label,
  existingConditions,
  onAddSpecialCondition,
  campaignId,
  buttonVariant = 'hollow',
  mode = 'modal',
  registerBeforeSave,
}: AddSpecialConditionButtonProps) {
  const [subClauseValidity, setSubClauseValidity] = useState<
    Record<string, boolean>
  >({})
  const defaultClauseNumber = useMemo(
    () => (existingConditions?.length ?? 0) + 1,
    [existingConditions?.length]
  )

  const [isAddSpecialConditionOpen, setIsAddSpecialConditionOpen] =
    useState<boolean>(false)
  const [isAddingSpecialCondition, setIsAddingSpecialCondition] =
    useState(false)
  const [triggerValidation, setTriggerValidation] = useState<boolean>(false)
  const [newSpecialCondition, setNewSpecialCondition] =
    useState<SpecialConditionData>(() =>
      getDefaultCustomCondition(defaultClauseNumber)
    )

  useEffect(() => {
    setTriggerValidation(false)
  }, [isAddSpecialConditionOpen])

  useEffect(() => {
    if (!isAddSpecialConditionOpen) return

    setSubClauseValidity({})
    setNewSpecialCondition(getDefaultCustomCondition(defaultClauseNumber))
  }, [isAddSpecialConditionOpen, defaultClauseNumber])

  const customConditionModel = useMemo(
    () => getModelFromCustomCondition(newSpecialCondition),
    [newSpecialCondition]
  )

  const clauseNameErrorText = useMemo(() => {
    if (!triggerValidation || customConditionModel?.textInput?.trim()) {
      return undefined
    }

    return 'Enter special condition name'
  }, [customConditionModel, triggerValidation])

  const clauseNumberErrorText = useMemo(() => {
    if (
      !triggerValidation ||
      (newSpecialCondition?.overriddenClauseNumber &&
        newSpecialCondition.overriddenClauseNumber > 0)
    ) {
      return undefined
    }

    return 'Enter a valid clause number'
  }, [newSpecialCondition, triggerValidation])

  const isValid = useMemo(() => {
    if (!newSpecialCondition) return false

    if (!customConditionModel?.textInput?.trim()) return false
    if (
      !newSpecialCondition?.overriddenClauseNumber ||
      newSpecialCondition.overriddenClauseNumber < 1
    )
      return false

    if (
      !customConditionModel?.subClauses ||
      customConditionModel.subClauses.length === 0
    )
      return false

    if (
      customConditionModel.subClauses?.some(
        (subClause) => !subClauseValidity[subClause.clauseId ?? '']
      )
    )
      return false

    return true
  }, [newSpecialCondition, customConditionModel, subClauseValidity])

  const validateAndAddSpecialCondition = useCallback(async () => {
    setTriggerValidation(true)

    if (!isValid || isAddingSpecialCondition) return false

    setIsAddingSpecialCondition(true)
    try {
      await onAddSpecialCondition(newSpecialCondition)
      setIsAddSpecialConditionOpen(false)
      return true
    } finally {
      setIsAddingSpecialCondition(false)
    }
  }, [
    isAddingSpecialCondition,
    isValid,
    newSpecialCondition,
    onAddSpecialCondition,
  ])

  const handleConfirmClick = useCallback(() => {
    void validateAndAddSpecialCondition()
  }, [validateAndAddSpecialCondition])

  const onAddConditionClicked = useCallback(() => {
    if (mode === 'inline' && isAddSpecialConditionOpen) {
      return
    }

    setNewSpecialCondition(getDefaultCustomCondition(defaultClauseNumber))
    setIsAddSpecialConditionOpen(true)
  }, [defaultClauseNumber, isAddSpecialConditionOpen, mode])

  useEffect(() => {
    if (mode !== 'inline' || !registerBeforeSave) return

    registerBeforeSave(
      isAddSpecialConditionOpen ? validateAndAddSpecialCondition : undefined
    )

    return () => registerBeforeSave(undefined)
  }, [
    isAddSpecialConditionOpen,
    mode,
    registerBeforeSave,
    validateAndAddSpecialCondition,
  ])

  const onCustomConditionNameChanged = useCallback((value: string) => {
    setNewSpecialCondition((prev) => {
      const currentCustom = getModelFromCustomCondition(prev)

      const updatedCustomModel = {
        ...currentCustom,
        textInput: value,
      }

      const updatedCondition = updateModelOnCustomCondition(
        updatedCustomModel,
        prev
      )

      return updatedCondition
    })
  }, [])

  const onClauseNumberChanged = useCallback((value: string) => {
    const parsedClauseNumber = Number.parseInt(value, 10)

    setNewSpecialCondition((prev) => ({
      ...prev,
      overriddenClauseNumber: Number.isNaN(parsedClauseNumber)
        ? undefined
        : parsedClauseNumber,
    }))
  }, [])

  const onSubClauseValidityChange = useCallback(
    (clauseId: string, isValid: boolean) => {
      setSubClauseValidity((prev) => ({
        ...prev,
        [clauseId]: isValid,
      }))
    },
    []
  )

  const customConditionName = useMemo(
    () => customConditionModel?.textInput ?? '',
    [customConditionModel]
  )

  const clauseNumberValue = useMemo(
    () => newSpecialCondition?.overriddenClauseNumber?.toString() ?? '',
    [newSpecialCondition]
  )

  const subClauses = useMemo(
    () => customConditionModel?.subClauses ?? [],
    [customConditionModel]
  )

  const onAddClause = useCallback(() => {
    const newSubClause: CustomConditionModel = {
      clauseId: window.crypto.randomUUID(),
      textInput: '',
    }

    setNewSpecialCondition((prev) => {
      const previousModel = getModelFromCustomCondition(prev)

      const updatedModel: CustomConditionModel = {
        ...previousModel,
        subClauses: [
          ...(previousModel?.subClauses ?? []),
          {
            ...newSubClause,
          },
        ],
      }

      const updatedCondition = updateModelOnCustomCondition(updatedModel, prev)

      return updatedCondition
    })
  }, [])

  const onSubClauseChange = useCallback(
    (updatedSubClause: CustomConditionModel) => {
      setNewSpecialCondition((prev) => {
        const previousModel = getModelFromCustomCondition(prev)

        const updatedModel: CustomConditionModel = {
          ...previousModel,
          subClauses: (previousModel?.subClauses ?? []).map((subClause) => {
            if (subClause.clauseId === updatedSubClause.clauseId) {
              return updatedSubClause
            }

            return subClause
          }),
        }

        const updatedCondition = updateModelOnCustomCondition(
          updatedModel,
          prev
        )

        return updatedCondition
      })
    },
    []
  )

  const onSubClauseDelete = useCallback((clauseId: string) => {
    setSubClauseValidity((prev) => {
      const nextValidity = { ...prev }

      delete nextValidity[clauseId]

      return nextValidity
    })

    setNewSpecialCondition((prev) => {
      const previousModel = getModelFromCustomCondition(prev)

      const updatedModel: CustomConditionModel = {
        ...previousModel,
        subClauses: (previousModel?.subClauses ?? []).filter(
          (clause) => clause.clauseId !== clauseId
        ),
      }

      const updatedCondition = updateModelOnCustomCondition(updatedModel, prev)

      return updatedCondition
    })
  }, [])

  const specialConditionForm = (
    <AgxSpacer
      direction="vertical"
      gap="md"
      padding={
        mode === 'modal'
          ? {
              top: 'spacing-3xl',
              bottom: 'spacing-3xl',
              left: 'xl',
              right: 'xl',
            }
          : 'spacing-2xl'
      }
      fill
    >
      <AgxTextInput
        key={`${newSpecialCondition?.id}-name`}
        id={`${id}-special-condition-name`}
        type="text"
        label={
          mode === 'inline'
            ? 'Details of Special Condition'
            : 'Special Condition Name'
        }
        required
        hideOptionalText
        placeholder="Enter special condition name"
        stretch
        defaultValue={customConditionName}
        onChange={onCustomConditionNameChanged}
        errorText={clauseNameErrorText}
      />
      <AgxTextInput
        key={`${newSpecialCondition?.id}-clause`}
        id={`${id}-clause-number`}
        type="number"
        label="Clause number"
        required
        hideOptionalText
        placeholder="Enter clause number"
        stretch
        defaultValue={clauseNumberValue}
        onChange={onClauseNumberChanged}
        errorText={clauseNumberErrorText}
      />
      <AgxSpacer direction="vertical" fill gap="xs">
        {subClauses.map((subclause, index) => {
          const clausePosition = `${newSpecialCondition?.overriddenClauseNumber ?? defaultClauseNumber}.${index + 1}`

          return (
            <CustomSpecialCondition
              key={`${newSpecialCondition?.id}-subclause-${subclause.clauseId}`}
              customCondition={subclause}
              onChange={onSubClauseChange}
              clausePosition={clausePosition}
              specialConditions={existingConditions}
              campaignId={campaignId}
              onDelete={onSubClauseDelete}
              onIsValidChange={onSubClauseValidityChange}
              triggerValidation={triggerValidation}
              hideDeleteButton={subClauses.length === 1}
            />
          )
        })}
        <AgxButton
          id={`${id}-add-clause-button`}
          text={'Add Clause'}
          variant={'primary'}
          size="medium"
          onClick={onAddClause}
        />
      </AgxSpacer>
    </AgxSpacer>
  )

  if (mode === 'inline') {
    return (
      <AgxSpacer direction="vertical" fill gap="spacing-md">
        {isAddSpecialConditionOpen && (
          <AgxCollapsible
            variant="withDivider"
            open
            enableToggle
            iconPosition="left"
            title={`Special Condition ${defaultClauseNumber}`}
            content={specialConditionForm}
            headerPadding={{
              top: 'spacing-xl',
              bottom: 'spacing-xl',
              left: 'spacing-2xl',
              right: 'spacing-2xl',
            }}
            isContentFit
          />
        )}
        <AgxButton
          id={`${id}-add-special-conditions`}
          variant={buttonVariant}
          text={label}
          size="medium"
          onClick={onAddConditionClicked}
          disabled={isAddingSpecialCondition}
        />
      </AgxSpacer>
    )
  }

  return (
    <>
      <AgxButton
        id={`${id}-add-special-conditions`}
        variant={buttonVariant}
        text={label}
        size="medium"
        onClick={onAddConditionClicked}
        disabled={isAddingSpecialCondition}
      />
      <AgxModal
        title="Add Special Condition"
        variant="withDivider"
        type="single"
        actionButtonContainerStyle="withDivider"
        maxWidth="600px"
        setIsDialogOpen={setIsAddSpecialConditionOpen}
        isOpen={isAddSpecialConditionOpen}
        content={specialConditionForm}
        actionButtons={
          <AgxSpacer direction="horizontal" gap="sm" fill>
            <AgxButton
              id={`${id}-back-add-special-condition`}
              variant="hollow"
              text="Back"
              size="large"
              stretch
              onClick={() => setIsAddSpecialConditionOpen(false)}
              disabled={isAddingSpecialCondition}
            />
            <AgxButton
              id={`${id}-confirm-add-special-condition`}
              variant="primary"
              text="Confirm"
              size="large"
              stretch
              onClick={handleConfirmClick}
              disabled={isAddingSpecialCondition}
            />
          </AgxSpacer>
        }
      />
    </>
  )
}
